<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import GenericDialog from '@/components/dialog/GenericDialog.vue'
import SpeckleLoginNoticeDialog from '@/components/dialog/SpeckleLoginNoticeDialog.vue'
import ProgressBar from '@/components/shared/ProgressBar.vue'
import { useGIDataSpeckleImport } from '@/composables/useGIDataSpeckleImport'
import { useSpeckleImportUIStore } from '@/stores/speckleImportUI'
import { useSpeckleSsoStore } from '@/stores/speckleSso'
import {
  SpeckleProjectSearch,
  SpeckleModelSelect,
  SpeckleVersionSelectField,
  useSpeckleAuthStore,
  useSpeckleStore
} from '@haskoning/vue-speckle'

const speckleImportUIStore = useSpeckleImportUIStore()
const { showDialog } = storeToRefs(speckleImportUIStore)
const { importGIData, importProgress } = useGIDataSpeckleImport()
const projectStore = useProjectStore()
const { currentItem: currentProject } = storeToRefs(projectStore)
const speckleStore = useSpeckleStore()
const speckleAuthStore = useSpeckleAuthStore()
const speckleSsoStore = useSpeckleSsoStore()
const { canUseSpeckle } = storeToRefs(speckleSsoStore)

const giDataCollectionName = ref('')
const isImporting = ref(false)
const importError = ref('')

watch(
  () => speckleStore.currentSelection.project,
  async (project) => {
    if (!project) {
      speckleStore.currentSelection.model = undefined
      return
    }
    await speckleStore.getSpeckleModels()
  }
)

watch(
  () => speckleStore.currentSelection.model,
  async (model) => {
    if (!model) {
      speckleStore.currentSelection.version = undefined
      return
    }
    await speckleStore.getSpeckleVersions()
  }
)

const isImportDisabled = computed(
  () =>
    isImporting.value ||
    !giDataCollectionName.value ||
    !speckleAuthStore.token ||
    !speckleStore.currentSelection.project?.id ||
    !speckleStore.currentSelection.model?.id ||
    !speckleStore.currentSelection.version?.id ||
    !currentProject.value?.id
)

function resetForm() {
  giDataCollectionName.value = ''
  speckleStore.currentSelection.project = undefined
  speckleStore.currentSelection.model = undefined
  speckleStore.currentSelection.version = undefined
}

function closeDialog() {
  importError.value = ''
  speckleImportUIStore.showDialog = false
}

async function handleReceive() {
  if (isImporting.value) {
    return
  }
  isImporting.value = true
  importError.value = ''

  try {
    const speckleProjectId = speckleStore.currentSelection.project?.id

    await importGIData({
      speckle_token: speckleAuthStore.token,
      speckle_project_id: speckleProjectId,
      speckle_model_id: speckleStore.currentSelection.model?.id,
      speckle_version_id: speckleStore.currentSelection.version?.id,
      project: currentProject.value?.id,
      description: `Imported from Speckle with id ${speckleProjectId}`,
      name: giDataCollectionName.value,
      collection_color: '#000000'
    })

    resetForm()
    closeDialog()
  } catch (error) {
    importError.value = error instanceof Error ? error.message : 'Import failed'
  } finally {
    isImporting.value = false
  }
}
</script>

<template>
  <SpeckleLoginNoticeDialog v-if="!canUseSpeckle" v-model="speckleImportUIStore.showDialog" />
  <GenericDialog :model-value="showDialog" v-else @clicked-outside="closeDialog">
    <template #title>Receive from Speckle</template>
    <template #default>
      <v-text-field
        v-model="giDataCollectionName"
        label="Name of GI Data Collection"
        color="primary"
        :rules="[(v: string) => !!v || 'Name is required']"
        :disabled="isImporting"
        hide-details="auto"
      ></v-text-field>
      <SpeckleProjectSearch class="mt-2" :disabled="isImporting" />
      <SpeckleModelSelect class="mt-2" variant="filled" :disabled="isImporting" />
      <SpeckleVersionSelectField class="mt-2" :disabled="isImporting" />
      <div v-if="isImporting" class="mt-4">
        <ProgressBar
          :percent="importProgress.percent"
          :indeterminate="importProgress.percent === null"
          :message="importProgress.message"
        />
      </div>
      <v-alert v-if="importError" type="error" variant="flat" class="mt-4">
        {{ importError }}
      </v-alert>
    </template>
    <template #actions>
      <v-btn variant="text" color="primary" @click="closeDialog"> Cancel </v-btn>
      <v-btn
        variant="text"
        color="primary"
        :loading="isImporting"
        :disabled="isImportDisabled"
        @click="handleReceive"
      >
        Receive
      </v-btn>
    </template>
  </GenericDialog>
</template>

<style scoped></style>
